import { useState } from 'react'
import { Home, Navigation, PauseCircle, Plane, PlaneTakeoff, Power, Send, ShieldAlert } from 'lucide-react'
import { Button, Field, Divider } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import type { MavCommand } from '../../types'

/* ============================================================================
 * УПРАВЛЕНИЕ БВС — MAVLink-КОМАНДЫ
 *
 * POST /api/mavcmd → { cmd, target, alt }
 * Команды: ARM, DISARM, TAKEOFF, RTL, HOLD
 *
 * Каждая команда перед отправкой показывает оператору подтверждение.
 * ========================================================================= */

interface CommandDef {
  cmd: MavCommand
  label: string
  icon: typeof Plane
  variant: 'default' | 'primary' | 'danger'
  /** Требуется высота взлёта */
  needsAlt?: boolean
  /** Команда меняет состояние вооружения — требует подтверждения */
  confirm?: boolean
}

const COMMANDS: CommandDef[] = [
  { cmd: 'ARM', label: 'Армирование', icon: Power, variant: 'primary', confirm: true },
  { cmd: 'DISARM', label: 'Снять с вооружения', icon: ShieldAlert, variant: 'danger', confirm: true },
  { cmd: 'TAKEOFF', label: 'Взлёт', icon: Plane, variant: 'primary', needsAlt: true, confirm: true },
  { cmd: 'RTL', label: 'Возврат (RTL)', icon: Home, variant: 'default', confirm: true },
  { cmd: 'HOLD', label: 'Удержание (HOLD)', icon: PauseCircle, variant: 'default' },
]

export function MavCmdPanel({
  onCommand,
  busy,
  lastCommand,
}: {
  onCommand: (cmd: MavCommand, alt: number) => void
  busy: boolean
  lastCommand: { cmd: MavCommand; ok: boolean; text: string } | null
}) {
  const [alt, setAlt] = useState(100)
  const [target, setTarget] = useState(1)
  const [pendingCmd, setPendingCmd] = useState<MavCommand | null>(null)

  /** Двухшаговая отправка: для критичных команд требуется подтверждение */
  const handle = (def: CommandDef): void => {
    if (def.confirm && pendingCmd !== def.cmd) {
      setPendingCmd(def.cmd)
      return
    }
    setPendingCmd(null)
    onCommand(def.cmd, def.needsAlt ? alt : 0)
  }

  return (
    <div className="space-y-2 p-2.5">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Высота взлёта, м" hint="Метры">
          <input
            type="number"
            className="ut-input ut-metric"
            min={0}
            max={150}
            value={alt}
            onChange={(e) => setAlt(Number(e.target.value))}
          />
        </Field>
        <Field label="Цель (target)">
          <input
            type="number"
            className="ut-input ut-metric"
            min={0}
            value={target}
            onChange={(e) => setTarget(Number(e.target.value))}
          />
        </Field>
      </div>

      <Divider label="Команды" />

      <div className="grid grid-cols-1 gap-1.5">
        {COMMANDS.map((def) => {
          const Icon = def.icon
          const isConfirming = pendingCmd === def.cmd
          return (
            <Button
              key={def.cmd}
              icon={isConfirming ? Send : Icon}
              variant={isConfirming ? 'danger' : def.variant}
              full
              disabled={busy}
              onClick={() => handle(def)}
            >
              {isConfirming ? `Подтвердить ${def.label}?` : def.label}
            </Button>
          )
        })}
      </div>

      {pendingCmd && (
        <div className="ut-stated ut-state-warning rounded-md border px-2 py-1.5 text-[11px]">
          <div className="flex items-center gap-1.5 font-semibold">
            <ShieldAlert size={12} aria-hidden />
            Требуется подтверждение
          </div>
          <div className="mt-0.5 text-ink-muted">
            Команда {pendingCmd} влияет на безопасность полёта. Повторное нажатие подтверждает
            отправку на {target}.
          </div>
          <button className="mt-1 text-[10px] underline" onClick={() => setPendingCmd(null)}>
            Отменить
          </button>
        </div>
      )}

      {lastCommand && (
        <div
          className={`rounded-md border px-2 py-1.5 text-[11px] ut-stated ${
            lastCommand.ok ? 'ut-state-approved' : 'ut-state-critical-error'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-semibold">Команда {lastCommand.cmd}</span>
            <StateBadge state={lastCommand.ok ? 'approved' : 'critical-error'}>
              {lastCommand.ok ? 'Отправлена' : 'Ошибка'}
            </StateBadge>
          </div>
          <div className="mt-0.5 text-[10px] text-ink-muted">{lastCommand.text}</div>
        </div>
      )}

      <div className="flex items-start gap-1.5 rounded-md border border-line px-2 py-1.5 text-[10px] leading-snug text-ink-muted">
        <PlaneTakeoff size={11} className="mt-0.5 shrink-0" aria-hidden />
        Команды исполняются только при подключённом БВС. При отсутствии телеметрии
        отправка завершится таймаутом — интерфейс покажет деградацию, а не зависнет.
      </div>
    </div>
  )
}

/** Экспортируем список команд для документации и тестов */
export const MAV_COMMANDS: MavCommand[] = ['ARM', 'DISARM', 'TAKEOFF', 'RTL', 'HOLD']

/** Иконка навигации для заголовка панели */
export const NavIcon = Navigation
